'use client';

import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { fetchUserDetail, type UserDetail } from '@/utils/profileApi';

const CACHE_TTL_MS = 5 * 60 * 1000;

type ProfileCacheEntry = {
  detail: UserDetail | null;
  fetchedAt: number;
};

const profileCache = new Map<string, ProfileCacheEntry>();
const inflightRequests = new Map<string, Promise<UserDetail | null>>();

async function loadUserDetail(accessToken: string): Promise<UserDetail | null> {
  const cached = profileCache.get(accessToken);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.detail;
  }

  const inflight = inflightRequests.get(accessToken);
  if (inflight) return inflight;

  const request = fetchUserDetail(accessToken)
    .then((detail) => {
      profileCache.set(accessToken, { detail, fetchedAt: Date.now() });
      return detail;
    })
    .finally(() => {
      inflightRequests.delete(accessToken);
    });

  inflightRequests.set(accessToken, request);
  return request;
}

export function invalidateSubscriptionProfileCache(accessToken?: string) {
  if (accessToken) {
    profileCache.delete(accessToken);
    inflightRequests.delete(accessToken);
    return;
  }
  profileCache.clear();
  inflightRequests.clear();
}

export function useSubscriptionStatus() {
  const { data: session, status } = useSession();
  const accessToken = (session?.user as { accessToken?: string } | undefined)?.accessToken;
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (status === 'loading') {
      return;
    }

    if (status !== 'authenticated' || !accessToken) {
      setIsSubscribed(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    loadUserDetail(accessToken)
      .then((detail) => {
        if (!cancelled) {
          setIsSubscribed(detail?.subscriptionStatus === 'active');
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsSubscribed(false);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, status]);

  return { isSubscribed, loading };
}
